import React, { useState, useEffect, useRef } from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { useUser } from '../../contexts/UserContext';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

interface SearchBarProps {
    value?: string;
    onSearchChange: (query: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ value = '', onSearchChange }) => {
    const { user } = useUser();
    const [searchTerm, setSearchTerm] = useState(value);
    const isInternalChange = useRef(false);

    // Sync from parent when value changes externally (e.g. URL restore on Back)
    useEffect(() => {
        if (!isInternalChange.current && value !== searchTerm) {
            setSearchTerm(value);
        }
        isInternalChange.current = false;
    }, [value]);

    const reportToParent = (q: string) => {
        isInternalChange.current = true;
        onSearchChange(q);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const v = event.target.value;
        setSearchTerm(v);
        reportToParent(v);
    };

    const handleClear = () => {
        setSearchTerm('');
        reportToParent('');
    };

    useEffect(() => {
        if (!user) {
            setSearchTerm('');
        }
    }, [user]);

    return (
        <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={handleChange}
            fullWidth
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon />
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            onClick={handleClear}
                            style={{ visibility: searchTerm ? 'visible' : 'hidden' }}
                        >
                            <ClearIcon />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );
};

export default SearchBar;
